import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { Heading } from "./outline";

/** Space kept above a heading scrolled to, so it does not touch the top edge. */
const TOP_GAP = 16;

/**
 * The note's title and headings beside the text. Choosing one scrolls only the note itself:
 * scrollIntoView would also scroll the page around the app and show a blank band below it.
 */
export function NoteOutline({
  title,
  headings,
  scroller,
  lineTop,
  onClose,
}: {
  title: string;
  headings: Heading[];
  scroller: () => HTMLElement | null;
  lineTop(line: number): number | null;
  onClose(): void;
}) {
  const [active, setActive] = useState<number | null>(null);
  const minLevel = Math.min(6, ...headings.map((h) => h.level));

  useEffect(() => {
    const el = scroller();
    if (!el) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const limit = el.getBoundingClientRect().top + TOP_GAP * 3;
      let current: number | null = null;
      for (const h of headings) {
        const top = lineTop(h.line);
        if (top === null || top > limit) break;
        current = h.line;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [headings, scroller, lineTop]);

  const go = (line: number | null) => {
    const el = scroller();
    if (!el) return;
    if (line === null) return el.scrollTo({ top: 0, behavior: "smooth" });
    const top = lineTop(line);
    if (top === null) return;
    el.scrollTo({ top: el.scrollTop + top - el.getBoundingClientRect().top - TOP_GAP, behavior: "smooth" });
  };

  return (
    <nav aria-label="Outline of the note" className="flex h-full flex-col">
      <div className="flex h-9 shrink-0 items-center justify-between gap-2 pr-1 pl-3 text-xs font-medium text-muted-foreground">
        Outline
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Hide the outline" title="Hide the outline">
          <X />
        </Button>
      </div>
      <ol className="min-h-0 flex-1 space-y-px overflow-y-auto px-2 pb-6 text-sm">
        <li>
          <button
            type="button"
            onClick={() => go(null)}
            className={cn("w-full rounded px-2 py-1 text-left font-semibold leading-snug hover:bg-accent", active === null && "text-primary")}
          >
            <span className="line-clamp-3">{title || "Untitled"}</span>
          </button>
        </li>
        {headings.map((h) => (
          <li key={h.line} style={{ paddingLeft: (h.level - minLevel) * 12 }}>
            <button
              type="button"
              onClick={() => go(h.line)}
              aria-current={active === h.line ? "location" : undefined}
              className={cn(
                "w-full rounded px-2 py-1 text-left leading-snug text-muted-foreground hover:bg-accent hover:text-foreground",
                h.level === minLevel && "text-foreground/90",
                active === h.line && "bg-primary/10 text-primary hover:text-primary",
              )}
            >
              <span className="line-clamp-2">{h.text}</span>
            </button>
          </li>
        ))}
        {headings.length === 0 && <li className="px-2 py-1 text-xs text-muted-foreground">Headings you add (# Title, ## Subtitle) appear here.</li>}
      </ol>
    </nav>
  );
}
